export default function loadTradeform() {
  const tradeForm = document.querySelector('#tradeForm'),
    formsHeader = document.querySelector('.forms-header'),
    submitButton = tradeForm.querySelector('#tradeSubmit'),
    instructions = formsHeader.querySelector('.instructions'),
    thankYou = formsHeader.querySelector('.thank-you');

  const documentInput = document.querySelector('#tradeDocuments');
  const documentList = document.querySelector('#tradeDocumentList');
  const documentError = document.querySelector('#tradeDocumentError');

  /* Account Type Selection */
  const tradeButton = tradeForm.querySelector("[data-toggle-value='trade']"),
    hospButton = tradeForm.querySelector("[data-toggle-value='hospitality']"),
    tradeCopy = tradeForm.querySelector("[data-toggle-group='trade']"),
    hospCopy = tradeForm.querySelector("[data-toggle-group='hospitality']");

  tradeButton.addEventListener('click', () => {
    tradeCopy.classList.remove('hide');
    hospCopy.classList.add('hide');
  });

  hospButton.addEventListener('click', () => {
    hospCopy.classList.remove('hide');
    tradeCopy.classList.add('hide');
  });

  /* Document Upload */
  let accumulatedFiles = new DataTransfer();

  /**
   * Re-renders the selected file list below the upload control.
   */
  const updateDocumentList = () => {
    documentList.innerHTML = '';
    for (const file of accumulatedFiles.files) {
      const li = document.createElement('li');
      li.className = 'document-upload__file-item';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'document-upload__file-name p6';
      nameSpan.textContent = file.name;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'document-upload__file-remove p6';
      removeBtn.textContent = 'Remove';
      removeBtn.setAttribute('aria-label', `Remove ${file.name}`);
      removeBtn.addEventListener('click', () => {
        const newDt = new DataTransfer();
        for (const f of accumulatedFiles.files) {
          if (f.name !== file.name) newDt.items.add(f);
        }
        accumulatedFiles = newDt;
        documentInput.files = accumulatedFiles.files;
        updateDocumentList();
      });

      li.appendChild(nameSpan);
      li.appendChild(removeBtn);
      documentList.appendChild(li);
    }
  };

  documentInput.addEventListener('change', () => {
    for (const file of documentInput.files) {
      accumulatedFiles.items.add(file);
    }
    documentInput.files = accumulatedFiles.files;
    updateDocumentList();
    documentError.textContent = '';
  });

  /* Spam Detection Helpers */
  /**
   * Heuristically detects random-looking spam strings (e.g. "qubMFnIYhL").
   * Each word is evaluated independently so legitimate multi-word entries
   * and short acronyms (e.g. "KSDesign LLC") aren't penalized by ratios
   * meant to catch single random tokens.
   * @param {string} text
   * @returns {boolean}
   */
  const isLikelyGibberish = (text) => {
    if (!text || text.trim().length < 3) return false;

    // Acronyms and short words are too small to judge reliably.
    const MIN_WORD_LENGTH = 6;

    return text
      .trim()
      .split(/\s+/)
      .some((word) => {
        if (word.length < MIN_WORD_LENGTH) return false;

        // Excessive consonant clusters (e.g., "qubMFnIYhL")
        if (/[bcdfghjklmnpqrstvwxyz]{7,}/i.test(word)) return true;

        // Alternating case within a single token (e.g., "qubMFnIYhL")
        if (/[a-z][A-Z].*[a-z][A-Z]/.test(word)) return true;

        // Vowel ratio over letters only (real words are usually 20-70% vowels)
        const letters = word.replace(/[^a-z]/gi, '');
        if (!letters.length) return false;
        const vowelRatio =
          (letters.match(/[aeiou]/gi) || []).length / letters.length;
        if (vowelRatio < 0.2 || vowelRatio > 0.7) return true;

        return false;
      });
  };

  const shouldContainSpaces = (name, value) => {
    // These fields should contain spaces if they're longer than 20 characters
    const fieldsNeedingSpaces = [
      'CompanyName',
      'CompanyAddressLine1',
      'CompanyAddressLine2',
    ];
    return (
      fieldsNeedingSpaces.includes(name) &&
      value.length > 20 &&
      !value.includes(' ')
    );
  };

  /**
   * Validates that at least one document is attached and all files are an
   * accepted type (JPG, PNG, PDF) under the 10 MB limit.
   * @returns {boolean}
   */
  const validateDocuments = () => {
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;
    const files = Array.from(accumulatedFiles.files);

    if (files.length === 0) {
      documentError.textContent =
        'Please upload at least one supporting document.';
      return false;
    }

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        documentError.textContent = `"${file.name}" is not an accepted format. Please upload JPG, PNG, or PDF files only.`;
        return false;
      }
      if (file.size > maxSize) {
        documentError.textContent = `"${file.name}" exceeds the 10 MB size limit.`;
        return false;
      }
    }

    documentError.textContent = '';
    return true;
  };

  /**
   * Builds a FormData payload from the current form state for the
   * services endpoint. Excludes the honeypot field.
   * @returns {FormData}
   */
  const buildFormData = () => {
    const formData = new FormData();

    for (const input of tradeForm.querySelectorAll("input[type='text']")) {
      if (input.id !== 'website_url') formData.append(input.name, input.value);
    }

    // API expects array notation for multi-value fields
    for (const inputName of ['ProductScope', 'HearAboutUs']) {
      for (const input of tradeForm.querySelectorAll(
        `input[name="${inputName}"]:checked`
      )) {
        formData.append(`${inputName}[]`, input.value);
      }
    }

    const taxExempt = tradeForm.querySelector(
      'input[name="TaxExempt"]:checked'
    );
    if (taxExempt) formData.append('TaxExempt', taxExempt.value);

    const accountType = tradeForm.querySelector(
      'input[name="accountType"]:checked'
    );
    if (accountType) formData.append('accountType', accountType.value);

    for (const file of accumulatedFiles.files) {
      formData.append('documents[]', file);
    }

    return formData;
  };

  /**
   * Posts a trade application to the services endpoint.
   * @param {FormData} formData
   * @returns {Promise<void>}
   */
  const submitApplication = async (formData) => {
    const response = await fetch(
      'https://services.brooklinen.com/api/v1/trade_applications',
      {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  };

  submitButton.addEventListener('click', async (e) => {
    e.preventDefault();

    if (submitButton.disabled) return;

    /* Honeypot Check - if filled, this is likely a bot */
    const honeypot = tradeForm.querySelector('#website_url');
    if (honeypot && honeypot.value !== '') return;

    /* Text input Required Validation */
    const textInputs = tradeForm.querySelectorAll("input[type='text']");
    const requiredInputs = Array.from(textInputs).filter((elem) =>
      elem.matches('[required]')
    );

    for (const elem of requiredInputs) {
      elem.value === ''
        ? elem.classList.add('invalid')
        : elem.classList.remove('invalid');
    }

    /* Gibberish Detection */
    // Skip gibberish detection for fields with specific formats (email, phone, URLs, addresses with abbreviations)
    const skipGibberishCheck = [
      'EmailAddress',
      'PhoneNumber',
      'CompanyLinksLine1',
      'CompanyLinksLine2',
      'CompanyAddressLine1',
      'CompanyAddressLine2',
    ];

    // Collect fields whose value looks like spam. A single consonant-heavy
    // entry (e.g. a real surname) is a weak signal on its own, so only treat
    // it as spam when multiple fields trip the check — real spam submissions
    // typically had gibberish across several fields, not just one.
    const GIBBERISH_FIELD_THRESHOLD = 2;
    const suspiciousFields = requiredInputs.filter((elem) => {
      if (skipGibberishCheck.includes(elem.name)) return false;
      const value = elem.value.trim();
      return (
        value &&
        (isLikelyGibberish(value) || shouldContainSpaces(elem.name, value))
      );
    });

    const hasGibberish = suspiciousFields.length >= GIBBERISH_FIELD_THRESHOLD;
    if (hasGibberish) {
      for (const elem of suspiciousFields) elem.classList.add('invalid');
    }

    const hasInvalidFields = tradeForm.querySelectorAll('.invalid').length > 0;
    const isValid = !hasInvalidFields && validateDocuments();

    // Show message if gibberish was detected across multiple fields
    if (hasGibberish) {
      alert(
        'Please enter valid information in all fields. Some entries appear to be invalid.'
      );
    }

    if (!isValid) return;

    submitButton.disabled = true;
    const originalButtonText = submitButton.textContent.trim();
    submitButton.textContent = 'Submitting...';

    // Force a microtask to allow rendering
    await new Promise((resolve) => setTimeout(resolve, 0));

    for (const input of tradeForm.querySelectorAll('input')) {
      input.setAttribute('disabled', '');
    }

    const enableInputs = () => {
      for (const input of tradeForm.querySelectorAll('input')) {
        input.removeAttribute('disabled');
      }
    };

    try {
      const formData = buildFormData();
      await submitApplication(formData);
      enableInputs();
      window.dataLayer.push({ event: 'dl_corporate_gifting_form_submit' });
      tradeForm.style.display = 'none';
      instructions.style.display = 'none';
      thankYou.style.display = 'block';
    } catch (error) {
      enableInputs();
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;
      throw new Error(`Form submission failed. ${error}`);
    }
  });
}
